import { orderBy, uniqBy } from 'lodash';
import { useCallback, useState } from 'react';
import { useLocalStorage } from 'react-use';

import { useRefetchOnAppResume } from '@/hooks/useRefetchOnAppResume';
import { useReportError } from '@/hooks/useReportError';
import { initialHouseholdSetupStorageKey } from '@/modules/onboarding/smartReview/initialHouseholdSetup';
import { useValidationQuestionsAnswers } from '@/modules/validationQuestions/ValidationQuestionsAnswers.context';
import { useHouseholdWorkflowTrackersContext } from '@/modules/workflowTrackers/contexts';
import { useFetchHouseholdSetupHistoryQuery } from '@/modules/workflowTrackers/graphql/HouseholdWorkflowTrackersContext.generated';
import { WorkflowTrackerStatus } from '@/types/schema';
import { getNodes } from '@/utils/graphqlUtils';

import { householdSetupPhase } from '../householdSetupPhase';
import { useHouseholdKGLoading } from './useHouseholdKGLoading';

/**
 * Derive setup state without selecting or changing chat. The overview is keyed
 * by householdId: changing households remounts this hook and its setup history.
 */
export function useHouseholdSetupPhase(householdId: string) {
  const kg = useHouseholdKGLoading(householdId);
  const { trackers, snapshotStatus } = useHouseholdWorkflowTrackersContext();
  const { reportError } = useReportError();
  // Read setup history in one request with separate per-kind limits. These
  // runs can outlive the shared snapshot's 50 recent trackers of all kinds.
  const setupHistory = useFetchHouseholdSetupHistoryQuery({
    variables: { householdID: householdId },
    fetchPolicy: 'no-cache',
    onError: (error) => {
      reportError('Failed to read household setup history', error, {
        householdId,
      });
    },
  });
  // Prefer the newest snapshot for each ID. Date truncates sub-millisecond
  // precision, so terminal states win ties, followed by started states.
  const setupTrackers = uniqBy(
    orderBy(
      [
        ...trackers,
        ...getNodes(setupHistory.data?.onboardingHistory),
        ...getNodes(setupHistory.data?.validationHistory),
        ...getNodes(setupHistory.data?.presentationHistory),
      ],
      [
        'updatedAt',
        (tracker) =>
          tracker.status === WorkflowTrackerStatus.Completed ||
          tracker.status === WorkflowTrackerStatus.Failed,
        (tracker) => tracker.status === WorkflowTrackerStatus.Started,
      ],
      ['desc', 'desc', 'desc']
    ),
    'id'
  );
  const [initialSetupTrackerId, , clearInitialSetup] = useLocalStorage<string>(
    initialHouseholdSetupStorageKey(householdId)
  );
  // Only a durable grouped response sets this, not a mutation acknowledgement.
  const [acceptedConversationId, setAcceptedConversationId] =
    useState<string>();
  const onAnswersAccepted = useCallback(
    (conversationId: string) => {
      setAcceptedConversationId(conversationId);
      // A reload before the household subscription catches up must not hide
      // the overview again after the server accepted the grouped response.
      clearInitialSetup();
    },
    [clearInitialSetup]
  );
  const answers = useValidationQuestionsAnswers(householdId);
  const { refetch: refetchSetupHistory } = setupHistory;
  useRefetchOnAppResume(refetchSetupHistory, {
    // Prefix for app-resume debug logs only; this is not a subscription ID.
    label: `HouseholdSetupHistory:${householdId}`,
  });
  const trackerSnapshot =
    snapshotStatus?.householdID === householdId ? snapshotStatus : undefined;
  return {
    ...householdSetupPhase({
      householdId,
      trackers: setupTrackers,
      ready:
        !!trackerSnapshot &&
        !!kg.snapshot &&
        answers.ready &&
        !!setupHistory.data,
      unavailable:
        !!trackerSnapshot?.unavailable ||
        kg.unavailable ||
        answers.unavailable ||
        !!setupHistory.error,
      documentExtractionHistoryIds: getNodes(
        setupHistory.data?.extractionHistory
      ).map((tracker) => tracker.id),
      extracting: kg.isLoading,
      validationQuestionsAnsweredAt: answers.answeredAt,
      acceptedConversationId,
      initialSetupTrackerId,
    }),
    snapshot: kg.snapshot,
    onAnswersAccepted,
  };
}
